// src/components/proposal/OrgChartView.tsx
// Renders the Management Approach "Organization Structure" as a readable hierarchical
// org chart instead of a raw `A -> B [tag]; ...` string. The generator emits reporting
// relationships separated by ';' (chains may be multi-hop, e.g. "A -> B -> C") followed
// by a narrative paragraph. We parse the relationships into a tree and render nested
// boxes with connector lines; any prose is shown beneath. Falls back to the raw text if
// it can't be parsed into edges.

import React from 'react';
import { User } from 'lucide-react';

interface TreeNode { label: string; tag: string; children: TreeNode[]; }

function splitNode(raw: string): { label: string; tag: string; narrative: string } {
  let s = raw.trim();
  const tagMatch = s.match(/\[([^\]]*)\]/);
  const tag = tagMatch ? tagMatch[1].trim() : '';
  let label = s.replace(/\[[^\]]*\]/g, '').trim();
  // A node label is short; if a full sentence trails it (". The …"), peel it as narrative.
  let narrative = '';
  const dot = label.search(/\.\s+[A-Z]/);
  if (dot !== -1) {
    narrative = label.slice(dot + 1).trim();
    label = label.slice(0, dot).trim();
  }
  label = label.replace(/^[.\s]+|[.\s]+$/g, '').trim();
  return { label, tag, narrative };
}

export function parseOrgChart(rawIn: string): { tree: TreeNode[]; narrative: string; edges: number } {
  const raw = String(rawIn || '');
  const nodes = new Map<string, { label: string; tag: string }>();
  const order: string[] = [];
  const childrenOf = new Map<string, string[]>();
  const childSet = new Set<string>();
  const narrativeParts: string[] = [];

  const register = (n: { label: string; tag: string }): string => {
    const key = n.label.toLowerCase();
    if (!key) return '';
    const existing = nodes.get(key);
    if (!existing) { nodes.set(key, { label: n.label, tag: n.tag }); order.push(key); }
    else if (n.tag && !existing.tag) existing.tag = n.tag;
    return key;
  };

  for (const clause of raw.split(';')) {
    if (!clause.includes('->')) {
      const t = clause.trim().replace(/^[.\s]+|[.\s]+$/g, '').trim();
      if (t) narrativeParts.push(t);
      continue;
    }
    const parts = clause.split('->').map(splitNode);
    parts.forEach((p) => { if (p.narrative) narrativeParts.push(p.narrative); });
    const keys = parts.map(register).filter(Boolean);
    for (let i = 0; i < keys.length - 1; i++) {
      const pk = keys[i], ck = keys[i + 1];
      if (!childrenOf.has(pk)) childrenOf.set(pk, []);
      if (!childrenOf.get(pk)!.includes(ck)) childrenOf.get(pk)!.push(ck);
      childSet.add(ck);
    }
  }

  const build = (key: string, ancestors: Set<string>): TreeNode => {
    const n = nodes.get(key)!;
    const kids = (childrenOf.get(key) || []).filter((k) => !ancestors.has(k));
    return { label: n.label, tag: n.tag, children: kids.map((k) => build(k, new Set([...ancestors, key]))) };
  };

  const roots = order.filter((k) => !childSet.has(k));
  const tree = roots.map((r) => build(r, new Set([r])));
  return { tree, narrative: narrativeParts.join(' '), edges: childSet.size };
}

function TagPill({ tag }: { tag: string }) {
  if (!tag) return null;
  const isKey = /key personnel/i.test(tag);
  return (
    <span
      className={`ml-2 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold align-middle ${
        isKey
          ? 'bg-bw-primary-tint text-bw-primary'
          : 'bg-bw-surface-subtle text-bw-muted dark:bg-gray-700 dark:text-gray-300'
      }`}
    >
      {tag}
    </span>
  );
}

function NodeBox({ node, depth }: { node: TreeNode; depth: number }) {
  return (
    <div>
      <div
        className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 shadow-sm ${
          depth === 0
            ? 'bg-bw-primary-tint/60 border-bw-primary/40 dark:bg-blue-900/30'
            : 'bg-bw-surface border-bw-border dark:bg-gray-800 dark:border-gray-700'
        }`}
      >
        <User className="h-4 w-4 text-bw-primary shrink-0" />
        <span className="text-sm font-semibold text-bw-ink dark:text-gray-100">{node.label}</span>
        <TagPill tag={node.tag} />
      </div>
      {node.children.length > 0 && (
        <div className="ml-4 mt-2 space-y-2 border-l-2 border-bw-border dark:border-gray-700 pl-4">
          {node.children.map((c, i) => (
            <NodeBox key={`${c.label}-${i}`} node={c} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

export function OrgChartView({ chart }: { chart: string }) {
  const { tree, narrative, edges } = React.useMemo(() => parseOrgChart(chart), [chart]);

  // Couldn't parse a hierarchy — show the original text rather than nothing.
  if (edges === 0 || tree.length === 0) {
    return (
      <pre className="text-sm text-bw-ink dark:text-gray-300 whitespace-pre-wrap font-mono">{chart}</pre>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2 overflow-x-auto">
        {tree.map((root, i) => (
          <NodeBox key={`${root.label}-${i}`} node={root} depth={0} />
        ))}
      </div>
      {narrative && (
        <p className="text-sm text-bw-ink dark:text-gray-300 leading-relaxed">{narrative}</p>
      )}
    </div>
  );
}

export default OrgChartView;
